import { useState } from 'react';
import { X, ZoomIn } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent } from '../components/ui/dialog';
import { galleryImages } from '../mock';

const Gallery = () => {
  const [selectedImage, setSelectedImage] = useState(null);

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1655761135788-a92466219d3e?w=1920&q=80"
            alt="Happy couple with dog"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-white/95 via-cyan-50/90 to-white"></div>
        </div>
        
        <div className="relative container mx-auto px-4 py-16 md:py-20">
          <div className="max-w-3xl mx-auto text-center">
            <Badge className="mb-4 bg-cyan-100 text-cyan-800 hover:bg-cyan-200 border-cyan-200">
              Memories
            </Badge>
            <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6 tracking-tight">
              Event Gallery
            </h1>
            <p className="text-lg text-slate-600">
              Relive the joy, excitement, and heartwarming moments from previous Paws in the Park events 
              and get a glimpse of what's to come in 2026!
            </p>
          </div>
        </div>
      </section>

      {/* Gallery Grid — masonry preserves aspect ratios */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            <div className="columns-1 sm:columns-2 lg:columns-3 gap-5 [column-fill:_balance]">
              {galleryImages.map((image) => (
                <button
                  type="button"
                  key={image.id}
                  onClick={() => setSelectedImage(image)}
                  className="group relative block w-full mb-5 break-inside-avoid overflow-hidden rounded-2xl border border-slate-200 hover:border-cyan-300 hover:shadow-2xl transition-all duration-300 text-left bg-slate-100"
                >
                  <img
                    src={image.url}
                    alt={image.caption}
                    loading="lazy"
                    className="w-full h-auto object-cover group-hover:scale-[1.03] transition-transform duration-700"
                  />

                  {/* Hover overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/85 via-slate-900/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="absolute bottom-0 left-0 right-0 p-5">
                      <p className="text-white text-sm md:text-base font-medium leading-snug">
                        {image.caption}
                      </p>
                    </div>
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
                      <div className="bg-white/95 rounded-full p-3 shadow-xl scale-90 group-hover:scale-100 transition-transform duration-300">
                        <ZoomIn className="h-6 w-6 text-slate-800" />
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Info Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6 tracking-tight">
              Create New Memories
            </h2>
            <p className="text-lg text-slate-600">
              Join us on September 6, 2026, and be part of the next chapter in our community's 
              celebration of hero dogs. Professional photography will be available to capture 
              your special moments!
            </p>
          </div>
        </div>
      </section>

      {/* Image Modal */}
      <Dialog open={!!selectedImage} onOpenChange={() => setSelectedImage(null)}>
        <DialogContent className="max-w-4xl p-0 overflow-hidden bg-slate-900 border-slate-700">
          {selectedImage && (
            <div className="relative">
              <button
                onClick={() => setSelectedImage(null)}
                className="absolute top-4 right-4 z-10 bg-white/90 hover:bg-white rounded-full p-2 transition-colors"
                aria-label="Close"
              >
                <X className="h-5 w-5 text-slate-800" />
              </button>
              
              <img
                src={selectedImage.url}
                alt={selectedImage.caption}
                className="w-full h-auto max-h-[80vh] object-contain"
              />
              
              <div className="p-6 bg-slate-900">
                <p className="text-white text-center text-lg">
                  {selectedImage.caption}
                </p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Gallery;
